import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SpaLink from './SpaLink';
import ProductsMenu from './ProductsMenu';
import {
  GAMES_UPDATED, GAMES_INTRO, HOW_IT_WORKS, LIVE_SCORING_NOTE, HOLE_SCREEN_CAPTION, BUILT_IN,
  EVENTS_NOTE, TURN_ON_NOTE, GAMES_PLAN, GROUP_GAMES, COMPETITIONS, GAME_COUNT, boldParts,
} from './gamesData';

/**
 * GamesPage — the public /games page (Oct 5, 2026 — Dennis: "the landing page should
 * reference games and have access to a page that looks like the fact sheet so visitors
 * can understand the current games that are available").
 *
 * The web version of the games fact sheet. Both read gamesData.js, so a game added
 * there shows here and in the next PDF build (Docs/Marketing/Games_Fact_Sheet). The
 * PDF itself is at /downloads/TTO_Games.pdf.
 *
 * Conventions follow EventsPage.js / LandingPage.js: inline style objects, an isMobile
 * flag, SpaLink for internal links, window.gtag for GA4. Prerendered for crawlers
 * (frontend/prerender.js STATIC_ROUTES + server.js PRERENDER_ROUTES).
 */

const PDF_URL = '/downloads/TTO_Games.pdf';
const CONTACT = 'support@teetimeorganizer.net';

const META = {
  title: 'Golf Games in Tee Time Organizer: Skins, Nassau, Sixes, Stableford and More',
  description: 'The golf games Tee Time Organizer sets up, scores live and settles: skins, Nassau, Sixes, Dots, putts, closest to the pin, stroke play, Stableford, best ball, shamble and captain’s choice.',
  url: 'https://teetimeorganizer.net/games',
};

const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') window.gtag('event', eventName, params);
};

const colors = {
  green900: '#14532d', green800: '#166534', green700: '#15803d', green600: '#16a34a',
  green200: '#bbf7d0', green100: '#dcfce7', green50: '#f0fdf4',
  gray900: '#111827', gray800: '#1f2937', gray700: '#374151', gray600: '#4b5563',
  gray500: '#6b7280', gray400: '#9ca3af', gray300: '#d1d5db', gray200: '#e5e7eb',
  gray100: '#f3f4f6', gray50: '#f9fafb',
  amber500: '#f59e0b', amber400: '#fbbf24', amber100: '#fef3c7', amber50: '#fffbeb',
};

const setMeta = (sel, attr, content) => { const el = document.querySelector(sel); if (el) el.setAttribute(attr, content); };

const Rich = ({ text }) => (
  <>{boldParts(text).map((p, i) => (p.bold ? <strong key={i}>{p.text}</strong> : <React.Fragment key={i}>{p.text}</React.Fragment>))}</>
);

const NewTag = () => (
  <span style={{ display: 'inline-block', background: colors.amber100, color: '#92400e', borderRadius: 5, padding: '1px 6px', fontSize: 11, fontWeight: 800, letterSpacing: 0.3, marginLeft: 6, verticalAlign: 2 }}>NEW</span>
);

const GameCard = ({ g }) => (
  <div id={`game-${g.name.toLowerCase().replace(/[^a-z]+/g, '-')}`} style={{ background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: 12, padding: '14px 16px', breakInside: 'avoid', marginBottom: 14 }}>
    <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 18, fontWeight: 800, color: colors.gray900, margin: '0 0 6px' }}>
      {g.cardName || g.name}{g.isNew && <NewTag />}
    </h3>
    <p style={{ fontSize: 15, color: colors.gray700, lineHeight: 1.6, margin: 0 }}><Rich text={g.intro} /></p>
    {g.bullets && (
      <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 15, color: colors.gray700, lineHeight: 1.55 }}>
        {g.bullets.map((b, i) => <li key={i} style={{ margin: '3px 0' }}><Rich text={b} /></li>)}
      </ul>
    )}
    {g.example && (
      <div style={{ marginTop: 10, background: colors.gray50, borderLeft: `3px solid ${colors.green600}`, padding: '6px 10px', fontSize: 14, color: colors.gray700 }}>
        <Rich text={g.example} />
      </div>
    )}
    {g.guide && (
      <SpaLink to={`/guides/${g.guide}`} onClick={() => trackEvent('games_guide_click', { game: g.name })} style={{ display: 'inline-block', marginTop: 10, fontSize: 14, fontWeight: 700, color: colors.green800, textDecoration: 'none' }}>
        How to set it up &rarr;
      </SpaLink>
    )}
  </div>
);

const GamesPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    document.title = META.title;
    setMeta('meta[name="description"]', 'content', META.description);
    setMeta('meta[name="title"]', 'content', META.title);
    setMeta('meta[property="og:title"]', 'content', META.title);
    setMeta('meta[property="og:description"]', 'content', META.description);
    setMeta('meta[property="og:url"]', 'content', META.url);
    setMeta('meta[name="twitter:title"]', 'content', META.title);
    setMeta('meta[name="twitter:description"]', 'content', META.description);
    setMeta('link[rel="canonical"]', 'href', META.url);
    window.scrollTo(0, 0);
  }, []);

  const pad = isMobile ? '0 18px' : '0 32px';
  const h2 = { fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? 28 : 34, fontWeight: 400, color: colors.gray900, margin: '0 0 6px', letterSpacing: '-0.5px' };
  const sub = { fontSize: 15, color: colors.gray500, margin: '0 0 22px' };
  const allGames = [...GROUP_GAMES, ...COMPETITIONS];

  const pdfButton = (location, dark = false) => (
    <a href={PDF_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('games_pdf_click', { location })} style={{
      display: 'inline-block', padding: '12px 20px', fontSize: 15, fontWeight: 700, borderRadius: 10, textDecoration: 'none',
      background: dark ? 'rgba(255,255,255,0.12)' : 'white', color: dark ? 'white' : colors.green800,
      border: dark ? '1px solid rgba(255,255,255,0.35)' : `2px solid ${colors.green700}`,
    }}>⬇ Download the fact sheet (PDF)</a>
  );

  return (
    <div style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", background: 'white', color: colors.gray800 }}>
      {/* Header */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.97)', borderBottom: `1px solid ${colors.gray200}`, padding: isMobile ? '10px 16px' : '12px 32px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <SpaLink to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <img src="/logo.png" alt="Tee Time Organizer" width="40" height="40" style={{ width: isMobile ? 34 : 40, height: isMobile ? 34 : 40, objectFit: 'contain' }} />
            <span style={{ fontSize: isMobile ? 15 : 18, fontWeight: 800, color: colors.gray900, letterSpacing: '-0.4px' }}>Tee Time Organizer</span>
          </SpaLink>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 8 }}>
            <ProductsMenu isMobile={isMobile} location="games_header" />
            {/* Phone: room for Products and Login only; Guides is in the footer. */}
            {!isMobile && <SpaLink to="/guides" style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, color: colors.gray700, textDecoration: 'none' }}>Guides</SpaLink>}
            <button onClick={() => navigate('/login')} style={{ padding: isMobile ? '8px 12px' : '10px 20px', fontSize: isMobile ? 12 : 14, fontWeight: 600, borderRadius: 10, border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white' }}>{isMobile ? 'Login' : 'Organizer Login'}</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ background: `linear-gradient(135deg, ${colors.green900} 0%, ${colors.green800} 100%)`, color: 'white', padding: isMobile ? '34px 18px' : '52px 32px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.14)', color: colors.green100, padding: '6px 14px', borderRadius: 50, fontSize: 12, fontWeight: 700, letterSpacing: 0.4, marginBottom: 14 }}>
            {GAME_COUNT} GAMES · {GAMES_PLAN.toUpperCase()} · UPDATED {GAMES_UPDATED.toUpperCase()}
          </div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? 34 : 46, fontWeight: 400, lineHeight: 1.1, margin: '0 0 12px', letterSpacing: '-0.5px' }}>Games in Tee Time Organizer</h1>
          <p style={{ fontSize: isMobile ? 16 : 18, lineHeight: 1.6, color: colors.green100, maxWidth: 700, margin: '0 0 22px' }}>{GAMES_INTRO}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 22 }}>
            {allGames.map(g => (
              <a key={g.name} href={`#game-${g.name.toLowerCase().replace(/[^a-z]+/g, '-')}`} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 50, padding: '6px 12px', fontSize: 14, fontWeight: 600, color: 'white', textDecoration: 'none' }}>
                {g.name}{g.isNew ? ' ✦' : ''}
              </a>
            ))}
          </div>
          {pdfButton('hero', true)}
        </div>
      </section>

      {/* How it works */}
      <section style={{ padding: isMobile ? '34px 0' : '48px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: pad }}>
          <h2 style={h2}>How it works</h2>
          <p style={sub}>From the first tee to settling up.</p>
          <div style={{ display: isMobile ? 'block' : 'grid', gridTemplateColumns: '1fr 260px', gap: 28, alignItems: 'start' }}>
            <div>
              {HOW_IT_WORKS.map((s, i) => (
                <div key={s.title} style={{ background: colors.green50, border: `1px solid ${colors.green200}`, borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ width: 26, height: 26, borderRadius: '50%', background: colors.green800, color: 'white', fontSize: 14, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: colors.green900 }}>{s.title}</span>
                  </div>
                  <p style={{ fontSize: 15, lineHeight: 1.6, color: colors.gray700, margin: 0 }}>{s.text}</p>
                </div>
              ))}
              <div style={{ background: colors.green800, color: 'white', borderRadius: 12, padding: '14px 16px', fontSize: 15, lineHeight: 1.6 }}>
                <strong>{LIVE_SCORING_NOTE.lead}</strong> {LIVE_SCORING_NOTE.text}
              </div>
            </div>
            <figure style={{ margin: isMobile ? '22px auto 0' : 0, maxWidth: 260, textAlign: 'center' }}>
              <img src="/games-hole-screen.png" alt="The TTO hole screen" width="642" height="1106" loading="lazy" style={{ width: '100%', height: 'auto', border: `8px solid ${colors.gray800}`, borderRadius: 26 }} />
              <figcaption style={{ fontSize: 13, color: colors.gray500, marginTop: 8 }}>{HOLE_SCREEN_CAPTION}</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Built into every game */}
      <section style={{ padding: isMobile ? '34px 0' : '48px 0', background: colors.gray50 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: pad }}>
          <h2 style={h2}>Built into every game</h2>
          <p style={sub}>The work nobody wants to do in the parking lot.</p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 14 }}>
            {BUILT_IN.map(b => (
              <div key={b.title} style={{ background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: colors.gray900, marginBottom: 4 }}>{b.title}</div>
                <p style={{ fontSize: 14.5, lineHeight: 1.6, color: colors.gray600, margin: 0 }}><Rich text={b.text} /></p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, background: colors.amber50, border: '1px solid #fde68a', borderRadius: 12, padding: '12px 16px', fontSize: 15, lineHeight: 1.6, color: '#78350f' }}>
            <strong>{EVENTS_NOTE.lead}</strong> {EVENTS_NOTE.text}{' '}
            <SpaLink to="/events" style={{ color: '#92400e', fontWeight: 700 }}>See Tournaments &amp; Outings &rarr;</SpaLink>
          </div>
        </div>
      </section>

      {/* At a glance */}
      <section style={{ padding: isMobile ? '34px 0' : '48px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: pad }}>
          <h2 style={h2}>The games at a glance</h2>
          <p style={sub}>How each game plays and pays.</p>
          {[['Group games — settled player to player', GROUP_GAMES], ['Competitions — the field is ranked and places are paid', COMPETITIONS]].map(([label, list]) => (
            <div key={label} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.5, color: colors.green800, textTransform: 'uppercase', margin: '0 0 6px' }}>{label}</div>
              {isMobile ? (
                list.map(g => (
                  <div key={g.name} style={{ borderBottom: `1px solid ${colors.gray200}`, padding: '10px 0' }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: colors.gray900 }}>{g.tableName || g.name}{g.isNew && <NewTag />}</div>
                    <div style={{ fontSize: 14, color: colors.gray600, lineHeight: 1.5 }}>{g.playAs} · {g.handicaps}</div>
                    <div style={{ fontSize: 14, color: colors.gray700, lineHeight: 1.5 }}>{g.money}</div>
                  </div>
                ))
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14.5 }}>
                  <thead>
                    <tr>{['Game', 'Play as', 'Handicaps', 'Money'].map(h => <th key={h} style={{ textAlign: 'left', background: colors.green800, color: 'white', padding: '8px 10px', fontWeight: 700 }}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {list.map(g => (
                      <tr key={g.name}>
                        <td style={{ padding: '8px 10px', borderBottom: `1px solid ${colors.gray200}`, fontWeight: 800, whiteSpace: 'nowrap' }}>{g.tableName || g.name}{g.isNew && <NewTag />}</td>
                        <td style={{ padding: '8px 10px', borderBottom: `1px solid ${colors.gray200}` }}>{g.playAs}</td>
                        <td style={{ padding: '8px 10px', borderBottom: `1px solid ${colors.gray200}` }}>{g.handicaps}</td>
                        <td style={{ padding: '8px 10px', borderBottom: `1px solid ${colors.gray200}` }}>{g.money}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Each game */}
      <section style={{ padding: isMobile ? '34px 0' : '48px 0', background: colors.gray50 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: pad }}>
          <h2 style={h2}>The games</h2>
          <p style={sub}>How each one is played in TTO.</p>
          {[['Group games', GROUP_GAMES], ['Competitions', COMPETITIONS]].map(([label, list]) => (
            <div key={label}>
              <h3 style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.5, color: colors.gray500, textTransform: 'uppercase', margin: '10px 0 10px' }}>{label}</h3>
              <div style={{ columnCount: isMobile ? 1 : 2, columnGap: 14 }}>
                {list.map(g => <GameCard key={g.name} g={g} />)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Call to action */}
      <section style={{ background: `linear-gradient(135deg, ${colors.green900} 0%, ${colors.green800} 100%)`, color: 'white', padding: isMobile ? '34px 18px' : '48px 32px', textAlign: 'center' }}>
        <h2 style={{ ...h2, color: 'white' }}>{TURN_ON_NOTE.lead}</h2>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: colors.green100, maxWidth: 640, margin: '0 auto 22px' }}>
          Plus ($9.99 a month, up to 40 regular players), Professional and League include every
          game, switched on for your group automatically. Already on Free or Starter? Upgrade any time
          under Settings &rarr; Subscription &amp; Billing. Step-by-step setup guides for every game are
          in <SpaLink to="/guides" style={{ color: 'white', fontWeight: 700 }}>Guides</SpaLink>. Questions?{' '}
          <a href={`mailto:${CONTACT}?subject=${encodeURIComponent('Games in Tee Time Organizer')}`} style={{ color: 'white', fontWeight: 700 }}>{CONTACT}</a>
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <SpaLink to="/register?tier=plus" onClick={() => trackEvent('games_signup_click', { tier: 'plus' })} style={{ display: 'inline-block', padding: '12px 24px', fontSize: 15, fontWeight: 700, borderRadius: 10, textDecoration: 'none', background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`, color: '#78350f' }}>Get started with Plus &rarr;</SpaLink>
          <SpaLink to="/groups#pricing" style={{ display: 'inline-block', padding: '12px 20px', fontSize: 15, fontWeight: 700, borderRadius: 10, textDecoration: 'none', background: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.35)' }}>Compare plans</SpaLink>
          {pdfButton('footer', true)}
        </div>
      </section>

      <footer style={{ padding: '22px 18px', textAlign: 'center', fontSize: 13, color: colors.gray500 }}>
        <SpaLink to="/" style={{ color: colors.gray600, textDecoration: 'none', margin: '0 10px' }}>Home</SpaLink>
        <SpaLink to="/groups" style={{ color: colors.gray600, textDecoration: 'none', margin: '0 10px' }}>Weekly Golf Groups</SpaLink>
        <SpaLink to="/events" style={{ color: colors.gray600, textDecoration: 'none', margin: '0 10px' }}>Tournaments &amp; Outings</SpaLink>
        <SpaLink to="/guides" style={{ color: colors.gray600, textDecoration: 'none', margin: '0 10px' }}>Guides</SpaLink>
        <SpaLink to="/privacy" style={{ color: colors.gray600, textDecoration: 'none', margin: '0 10px' }}>Privacy</SpaLink>
      </footer>
    </div>
  );
};

export default GamesPage;
